import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type {
  PublicFleaMarketDetail,
  PublicFleaMarketRow,
  ProducerFleaMarketOccurrence,
} from '@/types/fleaMarkets';
import { useFleaMarketRollout } from './useFleaMarketRollout';

export function usePublicProducerFleaMarkets(producerId: string | undefined) {
  const { config } = useFleaMarketRollout();

  return useQuery({
    queryKey: ['public-producer-flea-markets', producerId, config.public_visibility_enabled],
    queryFn: async (): Promise<PublicFleaMarketRow[]> => {
      if (!producerId || !config.public_visibility_enabled) return [];
      const { data, error } = await supabase.rpc('get_public_producer_flea_markets' as any, {
        p_producer_id: producerId,
      } as any);
      if (error) throw error;
      return (data ?? []) as PublicFleaMarketRow[];
    },
    enabled: !!producerId,
    staleTime: 30_000,
  });
}

export function usePublicPlaceFleaMarkets(placeId: string | undefined, limit = 4) {
  const { config } = useFleaMarketRollout();

  return useQuery({
    queryKey: ['public-place-flea-markets', placeId, limit, config.public_visibility_enabled],
    queryFn: async (): Promise<PublicFleaMarketRow[]> => {
      if (!placeId || !config.public_visibility_enabled) return [];
      const { data, error } = await supabase.rpc('get_public_place_flea_markets' as any, {
        p_place_id: placeId,
        p_limit: limit,
      } as any);
      if (error) throw error;
      return (data ?? []) as PublicFleaMarketRow[];
    },
    enabled: !!placeId,
    staleTime: 30_000,
  });
}

export function usePublicFleaMarketDetail(fleaMarketId: string | undefined) {
  const { config } = useFleaMarketRollout();

  return useQuery({
    queryKey: ['public-flea-market-detail', fleaMarketId, config.public_visibility_enabled],
    queryFn: async (): Promise<PublicFleaMarketDetail | null> => {
      if (!fleaMarketId || !config.public_visibility_enabled) return null;
      const { data, error } = await supabase.rpc('get_public_flea_market_detail' as any, {
        p_flea_market_id: fleaMarketId,
      } as any);
      if (error) throw error;
      const rows = (data ?? []) as PublicFleaMarketDetail[];
      return rows[0] ?? null;
    },
    enabled: !!fleaMarketId,
    staleTime: 30_000,
  });
}

export function usePublicFleaMarketOccurrences(fleaMarketId: string | undefined) {
  const { config } = useFleaMarketRollout();

  return useQuery({
    queryKey: ['public-flea-market-occurrences', fleaMarketId, config.public_visibility_enabled],
    queryFn: async (): Promise<ProducerFleaMarketOccurrence[]> => {
      if (!fleaMarketId || !config.public_visibility_enabled) return [];
      const { data, error } = await supabase.rpc('get_public_flea_market_occurrences' as any, {
        p_flea_market_id: fleaMarketId,
      } as any);
      if (error) throw error;
      return (data ?? []) as ProducerFleaMarketOccurrence[];
    },
    enabled: !!fleaMarketId,
    staleTime: 30_000,
  });
}
